import React, { Suspense, Fragment, useState, useEffect, useCallback, useRef } from 'react';
import LoadingView from './components/Loader/LoadingView';
import { SnackbarProvider } from 'notistack';
import isElectron from 'is-electron';
import { HashRouter, BrowserRouter, useLocation } from 'react-router-dom';
import RouteConfig from './routes';
import { ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { PayloadContext } from './context';
import { verifyToken } from './actions/userActions';
import BackgroundImageDev from './images/background.png';
import BackgroundImageProd from './images/background-prod.png';
import { PORTAL } from './config';

let Router;

if (isElectron())
	Router = HashRouter;
else
	Router = BrowserRouter;

const AppContent = ({ getPayload, data, isLoading }) =>
{
	const location = useLocation();

	useEffect(() =>
	{
		if (location.pathname === '/dashboard' || location.pathname === '/activation')
		{
			getPayload();
		}
	}, [ location.pathname, getPayload ]);

	return (
		<Fragment>
			<PayloadContext.Provider value={{ payload: data.payload, getPayload }}>
				{data.loading ? <LoadingView /> : <RouteConfig />}
				{isLoading && <LoadingView />}
			</PayloadContext.Provider>
		</Fragment>
	);
};

const MainApp = () =>
{
	const [ isLoading, setLoading ] = useState(false);
	const [ data, setData ] = useState({ loading: false, payload: null });

	const token = localStorage.getItem('userAuthToken');

	const isMountedRef = useRef(true);

	useEffect(() => () =>
	{
		isMountedRef.current = false;
	}, []);

	useEffect(() =>
	{
		// Set background image based on PORTAL value
		const element = document.getElementById('edumeet');

		element.style.backgroundImage = PORTAL === 'WORLD' ? `url(${BackgroundImageProd})` : `url(${BackgroundImageDev})`;

		// Set favicon based on PORTAL value
		const favicon = PORTAL === 'WORLD' ? '/favicon-prod.ico' : '/favicon.ico';

		const link = document.createElement('link');

		link.rel = 'icon';
		link.href = favicon;
		document.head.appendChild(link);

		// Cleanup: remove the favicon link on component unmount if needed
		return () =>
		{
			document.head.removeChild(link);
		};
	}, []);

	const getPayload = useCallback((status = false) =>
	{
		if (token && token !== '')
		{
			if (status)
			{
				setLoading(true);
			}
			else
			{
				setData((prevState) =>
				{
					return { ...prevState, loading: true };
				});
			}
			verifyToken()
				.then((res) =>
				{
					if (!isMountedRef.current) return;

					if (res?.data?.success && res?.data?.msg)
					{
						if (status)
						{
							setLoading(false);
							setData({ payload: res.data.msg, loading: false });
						}
						else
						{
							setData({ payload: res.data.msg, loading: false });
						}
					}
					else if (status)
					{
						setLoading(false);
					}
					else
					{
						setData({ payload: null, loading: false });
					}
				})
				.catch((err) =>
				{
					// console.log('err in verifyToken ===== ', err);
					if (!isMountedRef.current) return;

					if (status)
					{
						setLoading(false);
					}
					else
					{
						setData({ payload: null, loading: false });
					}
				});
		}
		else
		{
			setData({ loading: false, payload: null });
		}
	}, [ token ]);

	useEffect(() =>
	{
		if (token && token !== '')
		{
			getPayload();
		}
		// console.log('token api call check ');
	}, [ token, getPayload ]);

	useEffect(() =>
	{
		const canvas = document.createElement('canvas');
		const gl = canvas.getContext('webgl') || canvas.getContext('experimental-webgl');

		// if (gl && gl instanceof WebGLRenderingContext) {
		// 	dispatch(meActions.setWebGLSupport(true));
		// }
	}, []);

	return (
		<SnackbarProvider>
			<Router>
				<Suspense fallback={<LoadingView />}>
					<AppContent getPayload={getPayload} data={data} isLoading={isLoading} />
				</Suspense>
			</Router>
			<ToastContainer />
		</SnackbarProvider>
	);
};

export default MainApp;
